import { useEffect, useState } from 'react';
import { Label } from '@/components/ui/label';
import { SelectMenu } from '@/components/ui/select-menu';
import { useToast } from '@/components/ui/use-toast';
import StaffAPI from '@/api/staff.api';

const formatOwnerLabel = owner => {
  const name = [owner.firstName, owner.lastName].filter(Boolean).join(' ').trim();
  return name ? `${name} — ${owner.email}` : owner.email;
};

/**
 * Shared alt-owner picker for the staff Update / Delete subscription dialogs.
 * Lazily fetches eligible COLLABORATOR+ users in the subscription's org and renders
 * a select-menu, a loading state, or an explanatory empty state.
 *
 * Parent owns the picked id (via `value` / `onChange`) and the gating logic on
 * whether an alt owner is needed at all (`active`).
 */
export default function AltOwnerPicker({
  active,
  userId,
  subscriptionId,
  value,
  onChange,
  disabled,
  onLoadingChange,
  labelId = 'alt-owner',
  label = 'Fallback owner',
}) {
  const [eligibleOwners, setEligibleOwners] = useState([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!active || !subscriptionId) {
      setEligibleOwners([]);
      onLoadingChange?.(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    onLoadingChange?.(true);
    StaffAPI.getSubscriptionEligibleOwners({ userId, subscriptionId })
      .then(res => {
        if (cancelled) return;
        if (res.error) {
          toast({
            title: 'Failed to load eligible owners',
            description: res.data?.message || res.error.message || 'Unexpected error',
            variant: 'destructive',
          });
          setEligibleOwners([]);
          return;
        }
        setEligibleOwners(res.data?.owners ?? []);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        onLoadingChange?.(false);
      });

    return () => {
      cancelled = true;
      // Always clear the parent's loading state on cleanup; if the picker
      // unmounts mid-fetch, the `.finally` skips its reset (cancelled === true),
      // which would leak fetchingOwners=true upstream and keep Save disabled.
      onLoadingChange?.(false);
    };
  }, [active, userId, subscriptionId, toast, onLoadingChange]);

  if (!active) return null;

  const hasEligible = eligibleOwners.length > 0;

  return (
    <div className="space-y-2">
      <Label htmlFor={labelId}>{label}</Label>
      {loading ? (
        <div className="text-xs text-muted-foreground">Loading eligible owners...</div>
      ) : hasEligible ? (
        <SelectMenu
          id={labelId}
          value={value || undefined}
          onValueChange={onChange}
          disabled={disabled}
          placeholder="Select a fallback owner..."
          options={eligibleOwners.map(owner => ({
            value: owner.id,
            label: formatOwnerLabel(owner),
          }))}
        />
      ) : (
        <div className="rounded-md border border-destructive/40 p-3 bg-destructive/5 text-xs text-destructive">
          No one in this organization can take over as owner. Grant someone COLLABORATOR or higher
          here first, then come back.
        </div>
      )}
    </div>
  );
}
